import { useCallback, useMemo } from 'react';

import AutocompleteWithChips from 'components/AutocompleteWithChips/AutocompleteWithChips';
import { LookupDateRangeQueryItemDto } from 'types/CodegenLookupTypes';
import { SelectedItemDto } from 'types/CodegenReportTypes';
import { usePostLookupUmpires } from 'hooks/CodegenLookupHooks';
import { useTranslation } from 'react-i18next';

type Props = {
  selectedUmpires: SelectedItemDto[] | undefined;
  onChange: (umpires: SelectedItemDto[]) => void;
  dateRanges: LookupDateRangeQueryItemDto[] | undefined;
  leagueIds: string[] | undefined;
};

const SelectUmpires = ({ selectedUmpires, onChange, dateRanges, leagueIds }: Props) => {
  const { t } = useTranslation();
  const { data, isLoading } = usePostLookupUmpires(dateRanges, leagueIds);

  const allUmpireIds = useMemo(() => data?.map((datum) => datum.id) ?? [], [data]);
  const getUmpireName = useCallback(
    (umpireId: string) => new Map(data?.map((datum) => [datum.id, datum.name])).get(umpireId) ?? (t('Nonexistent umpire') as string),
    [data]
  );

  return (
    <AutocompleteWithChips
      label={t('Umpires')}
      options={allUmpireIds}
      selected={selectedUmpires?.map((umpire) => umpire.id) ?? []}
      setSelected={(selectedIds) =>
        onChange(selectedIds.map((id) => data?.find((datum) => datum.id === id)).filter(Boolean) as SelectedItemDto[])
      }
      getOptionLabel={(option) => (isLoading ? t('Loading umpires...') : getUmpireName(option))}
      noOptionsText={isLoading ? t('Loading umpires...') : t('No umpires found')}
      disabled={!dateRanges || !leagueIds}
    />
  );
};

export default SelectUmpires;
